﻿import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedNumber } from "react-intl";
import { Link } from "react-router-dom";
import type { Invoice as ApiInvoice, VendorDocument } from "randmar-api-client";
import { Badge, Checkbox, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import type { Invoice as LegacyInvoice } from "../../stores/document/Document";
import CurrencyUtils from "../util/CurrencyUtils";
import FormatDateFromInt from "../util/DateTimeUtil";
import { TablePaginationBar } from "../util/TablePaginationBar";

interface IProps {
  invoices?: Array<LegacyInvoice | ApiInvoice | VendorDocument>;
  defaultRowsPerPage?: number;
}

interface InvoiceSummaryRow {
  invoiceNumber: string;
  poNumber?: string;
  documentType?: string;
  invoiceDate?: number;
  subTotal: number;
  taxAmount1: number;
  taxRate1: number;
  taxAmount2: number;
  taxRate2: number;
  total: number;
  payed?: boolean;
}

const rowsPerPageOptions = [10, 20, 50, 100] as const;

const isLegacyInvoice = (invoice: LegacyInvoice | ApiInvoice | VendorDocument): invoice is LegacyInvoice =>
  "invoiceNumber" in invoice;

type ModernInvoice = ApiInvoice | VendorDocument;

const pickRepresentativeInvoice = (lines: ModernInvoice[]): ModernInvoice => {
  return lines.reduce((best, current) => {
    const bestScore = Math.abs(best.Total ?? best.SubTotal ?? 0);
    const currentScore = Math.abs(current.Total ?? current.SubTotal ?? 0);
    return currentScore > bestScore ? current : best;
  }, lines[0]);
};

const getInvoiceDocumentType = (lines: ModernInvoice[]): string | undefined => {
  const docType = lines
    .map((line) => ("DocumentType" in line ? line.DocumentType : undefined))
    .find((value): value is string => Boolean(value && value.trim()));
  if (docType) return docType;

  return lines
    .map((line) => ("LineType" in line ? line.LineType : undefined))
    .find((value): value is string => Boolean(value && value.trim())) ?? undefined;
};

const getInvoicePoNumber = (invoice: ModernInvoice): string | undefined => {
  if ("PONumber" in invoice && invoice.PONumber) return invoice.PONumber;
  if ("PurchaseOrderNumber" in invoice && invoice.PurchaseOrderNumber) return invoice.PurchaseOrderNumber;
  return undefined;
};

const normalizeInvoicePayed = (payed: boolean | number | undefined): boolean | undefined => {
  if (typeof payed === "boolean") return payed;
  if (typeof payed === "number") return payed !== 0;
  return undefined;
};

const InvoicesGrid: React.FC<IProps> = ({ invoices, defaultRowsPerPage = 20 }) => {
  const { applicationId } = useManufacturer();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const userOverrodeUnpaidFilter = useRef(false);
  const [showOnlyUnpaid, setShowOnlyUnpaid] = useState<boolean | undefined>(undefined);

  const summarizedInvoices = useMemo<InvoiceSummaryRow[]>(() => {
    const legacy = (invoices ?? []).filter(isLegacyInvoice);
    const modern = (invoices ?? []).filter((value): value is ModernInvoice => !isLegacyInvoice(value));

    const modernGrouped = new Map<string, ModernInvoice[]>();
    for (const line of modern) {
      const invoiceNumber = line.InvoiceNumber ?? "";
      if (!invoiceNumber) {
        continue;
      }
      const list = modernGrouped.get(invoiceNumber);
      if (!list) {
        modernGrouped.set(invoiceNumber, [line]);
      } else {
        list.push(line);
      }
    }

    const modernSummaries: InvoiceSummaryRow[] = Array.from(modernGrouped.values()).map((lines) => {
      const rep = pickRepresentativeInvoice(lines);
      return {
        invoiceNumber: rep.InvoiceNumber ?? "-",
        poNumber: getInvoicePoNumber(rep),
        documentType: getInvoiceDocumentType(lines),
        invoiceDate: rep.InvoiceDate ?? undefined,
        subTotal: rep.SubTotal ?? 0,
        taxAmount1: rep.TaxAmount1 ?? 0,
        taxRate1: rep.TaxRate1 ?? 0,
        taxAmount2: rep.TaxAmount2 ?? 0,
        taxRate2: rep.TaxRate2 ?? 0,
        total: rep.Total ?? 0,
        payed: normalizeInvoicePayed(rep.Payed),
      };
    });

    const legacySummaries: InvoiceSummaryRow[] = legacy
      .filter((invoice) => Boolean(invoice.invoiceNumber))
      .map((invoice) => ({
        invoiceNumber: invoice.invoiceNumber || "-",
        poNumber: invoice.purchaseOrderNumber || undefined,
        documentType: invoice.documentType || undefined,
        invoiceDate: invoice.invoiceDate ?? undefined,
        subTotal: invoice.subTotal ?? 0,
        taxAmount1: invoice.taxAmount1 ?? 0,
        taxRate1: invoice.taxRate1 ?? 0,
        taxAmount2: invoice.taxAmount2 ?? 0,
        taxRate2: invoice.taxRate2 ?? 0,
        total: invoice.total ?? 0,
        payed: invoice.payed,
      }));

    return [...legacySummaries, ...modernSummaries].sort(
      (a, b) =>
        (b.invoiceDate ?? 0) - (a.invoiceDate ?? 0) ||
        b.invoiceNumber.localeCompare(a.invoiceNumber)
    );
  }, [invoices]);

  const defaultShowOnlyUnpaid = useMemo(() => {
    const invoiceCount = summarizedInvoices.length;
    if (invoiceCount === 0) return false;

    const unpaidCount = summarizedInvoices.filter((invoice) => invoice.payed === false).length;
    if (unpaidCount === 0) return false;

    if (invoiceCount > 20) return true;

    return false;
  }, [summarizedInvoices]);

  useEffect(() => {
    if (userOverrodeUnpaidFilter.current) return;
    setShowOnlyUnpaid(defaultShowOnlyUnpaid);
  }, [defaultShowOnlyUnpaid]);

  const effectiveShowOnlyUnpaid = showOnlyUnpaid ?? defaultShowOnlyUnpaid;

  const filteredInvoices = useMemo(
    () => summarizedInvoices.filter((invoice) => (effectiveShowOnlyUnpaid ? invoice.payed === false : true)),
    [summarizedInvoices, effectiveShowOnlyUnpaid]
  );

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredInvoices.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(0);
  }, [filteredInvoices.length, page, rowsPerPage]);

  const toggleUnpaidOnly = (checked: boolean | "indeterminate") => {
    userOverrodeUnpaidFilter.current = true;
    setPage(0);
    setShowOnlyUnpaid(checked === true);
  };

  return (
    <section className="w-full">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-[20px] font-medium text-slate-900">Invoices</h3>
          <p className="text-[15px] text-slate-600">
            Review invoice balances and taxes. Use the toggle to focus on unpaid items.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Checkbox checked={effectiveShowOnlyUnpaid} onCheckedChange={toggleUnpaidOnly} />
          Show only unpaid
        </label>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-100">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow className="h-14 hover:bg-gray-50">
              <TableHead className="w-[130px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Status
              </TableHead>
              <TableHead className="min-w-[160px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Type
              </TableHead>
              <TableHead className="min-w-[170px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                PO number
              </TableHead>
              <TableHead className="min-w-[170px] text-xs font-semibold uppercase tracking-wide text-slate-500">
                Invoice
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Date
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Subtotal
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Tax 1
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Tax 2
              </TableHead>
              <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Total
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInvoices.length ? (
              filteredInvoices
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const statusVariant = row.payed ? "success" : "warning";
                  const statusLabel = row.payed ? "Paid" : "Unpaid";

                  return (
                    <TableRow
                      key={`${row.invoiceNumber}-${row.poNumber ?? ""}-${row.invoiceDate ?? 0}`}
                      className="h-14 border-b border-gray-100 hover:bg-gray-50/60"
                    >
                      <TableCell>
                        <Badge variant={statusVariant} size="sm">
                          {statusLabel}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-700">{row.documentType || "-"}</TableCell>
                      <TableCell className="font-semibold">
                        {row.poNumber ? (
                          <Link
                            className="text-slate-900 underline-offset-4 hover:text-[color:var(--ops-accent)] hover:underline"
                            to={Path.document(applicationId, row.poNumber)}
                          >
                            {row.poNumber}
                          </Link>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-700">{row.invoiceNumber}</TableCell>
                      <TableCell className="text-slate-700">{FormatDateFromInt(row.invoiceDate)}</TableCell>
                      <TableCell className="text-right font-medium text-slate-900">
                        <FormattedNumber {...CurrencyUtils.format} value={row.subTotal} />
                      </TableCell>
                      <TableCell className="text-right text-slate-700">
                        {row.taxAmount1 || row.taxRate1 ? (
                          <span className="whitespace-nowrap">
                            <FormattedNumber {...CurrencyUtils.format} value={row.taxAmount1} />
                            {row.taxRate1 ? (
                              <span className="ml-1 text-xs text-slate-500">({row.taxRate1}%)</span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-slate-700">
                        {row.taxAmount2 || row.taxRate2 ? (
                          <span className="whitespace-nowrap">
                            <FormattedNumber {...CurrencyUtils.format} value={row.taxAmount2} />
                            {row.taxRate2 ? (
                              <span className="ml-1 text-xs text-slate-500">({row.taxRate2}%)</span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium text-slate-900">
                        <FormattedNumber {...CurrencyUtils.format} value={row.total} />
                      </TableCell>
                    </TableRow>
                  );
                })
            ) : (
              <TableRow className="h-20">
                <TableCell colSpan={9} className="py-10 text-center text-slate-500">
                  {summarizedInvoices.length === 0
                    ? "There are no invoices to display right now."
                    : effectiveShowOnlyUnpaid
                      ? "No unpaid invoices match the current view."
                      : "No invoices match the current view."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {filteredInvoices.length > rowsPerPage && (
        <TablePaginationBar
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={filteredInvoices.length}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={(nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(nextRows) => setRowsPerPage(nextRows)}
        />
      )}
    </section>
  );
};

export default InvoicesGrid;


